import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import { setWorkspaceIdGetter } from "../services/api";
import { fetchUserWorkspaces, selectWorkspaceApi, createWorkspaceApi } from "../services/workspace-management";
import { socket, setSocketPermissions, updateSocketWorkspace } from "../services/socket";

export const WorkspaceContext = createContext();

export function WorkspaceProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentUserRole = currentWorkspace?.role || null;
  const currentPermissions = currentWorkspace?.permissions || [];

  const hasPermission = useCallback((permission) => {
    if (currentUserRole === "owner") return true;
    if (currentUserRole === "guest") return false;
    return currentPermissions.includes(permission);
  }, [currentUserRole, currentPermissions]);

  const selectWorkspace = useCallback((ws) => {
    selectWorkspaceApi(ws._id).catch(() => {});
    setCurrentWorkspace(ws);
    updateSocketWorkspace(ws._id);
  }, []);

  const closeWorkspace = useCallback(() => {
    if (socket && socket.connected && currentWorkspace) {
      socket.emit("close_workspace", currentWorkspace._id);
    }
    selectWorkspaceApi(null).catch(() => {});
    setCurrentWorkspace(null);
  }, [currentWorkspace]);

  const leaveWorkspace = useCallback(() => {
    if (socket && socket.connected && currentWorkspace) {
      socket.emit("leave_workspace", currentWorkspace._id);
    }
    selectWorkspaceApi(null).catch(() => {});
    setCurrentWorkspace(null);
  }, [currentWorkspace]);

  const createWorkspace = useCallback(async (nom, description) => {
    const data = await createWorkspaceApi(nom, description);
    if (data.workspace) {
      setWorkspaces((prev) => [...prev, data.workspace]);
      selectWorkspace(data.workspace);
    }
    return data;
  }, [selectWorkspace]);

  useEffect(() => {
    setWorkspaceIdGetter(() => currentWorkspace?._id || null);
    if (currentWorkspace?._id) {
      updateSocketWorkspace(currentWorkspace._id);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (!user) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      return;
    }

    let cancelled = false;

    fetchUserWorkspaces()
      .then(({ workspaces: list, currentWorkspaceId }) => {
        if (cancelled) return;
        setWorkspaces(list);

        if (currentWorkspaceId) {
          const match = list.find((w) => w._id === currentWorkspaceId);
          if (match) setCurrentWorkspace(match);
        }
      })
      .catch(() => {
        if (!cancelled) setWorkspaces([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user]);

  // Écouter les mises à jour de permissions en temps réel
  useEffect(() => {
    if (!socket) return;

    const handlePermissionsUpdated = ({ userId: updatedUserId, role, permissions }) => {
      // Appliquer UNIQUEMENT si l'événement concerne l'utilisateur courant
      if (updatedUserId && updatedUserId !== user?.userId) return;

      setSocketPermissions(role, permissions);
      setCurrentWorkspace((prev) => {
        if (!prev) return prev;
        return { ...prev, role, permissions };
      });
    };

    socket.on("workspace_permissions_updated", handlePermissionsUpdated);
    return () => {
      socket.off("workspace_permissions_updated", handlePermissionsUpdated);
    };
  }, [socket, user?.userId]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspace,
        currentUserRole,
        currentPermissions,
        hasPermission,
        selectWorkspace,
        closeWorkspace,
        leaveWorkspace,
        createWorkspace,
        loading,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}
